import React, { useState, Dispatch, SetStateAction, useEffect } from "react";
import {
  Modal,
  Button,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  Autocomplete,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditText from "../components/editText";
import {
  useLazyAllMembersQuery,
  useLazyGetMemberGroupsQuery,
  useLazyGetUsersQuery,
} from "../service/user";
import { showError, showToast } from "../constants/toasts";
import { Categories, Group, Members } from "../types/General";
import {
  useAddNotificationMutation,
  useLazyGetActiveOffersQuery,
  useLazyGetDeviceSpecificUsersQuery,
} from "../service/notification";
import { NOTIFICATION_TYPES } from "../utils/enums";
import { useLazyAllEmployeesQuery } from "../service/booking";
import { useLazyGetCategoryQuery } from "../service/category";
import { useLazyGetOffersQuery } from "../service/offer";
import { offerReducer } from "../reducers";

interface AddNotificationModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  getNotificationsApi: () => void;
}

interface EmployeeType {
  email: string;
  id: string;
  name: string;
}

const AddNotification: React.FC<AddNotificationModalProps> = ({
  open,
  onClose,
  setOpen,
  getNotificationsApi,
}) => {
  const [getUsers] = useLazyAllMembersQuery();
  const [getGroups] = useLazyGetMemberGroupsQuery();
  const [addNotification] = useAddNotificationMutation();
  const [deviceSpecificUsers] = useLazyGetDeviceSpecificUsersQuery();
  const [employees] = useLazyAllEmployeesQuery();
  const [getCategory] = useLazyGetCategoryQuery();
  const [getOffers] = useLazyGetOffersQuery();
  const [getActiveOffers] = useLazyGetActiveOffersQuery();

  const [users, setUsers] = useState<Members[]>([]);
  const [groupData, setGroupData] = useState<Group[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string[]>([]);
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(true);
  const [selectedType, setSelectedType] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [loading, setLoading] = useState(false);
  const [legal3, setLegal3] = useState<string>("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [deviceUserData, setDeviceUserData] = useState<Members[]>([]);
  const [audience, setAudience] = useState("");
  const [employeeData, setEmployeeData] = useState<EmployeeType[]>([]);
  const [categoryData, setCategoryData] = useState<Categories[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    ""
  );
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>("");
  const [selectedActiveOffer, setSelectedActiveOffer] = useState<string | null>(
    ""
  );
  const [offerData, setOfferData] = useState([]);
  const [activeOfferData, setActiveOfferData] = useState([]);

  const clear = () => {
    setSelectedUsers([]);
    setSelectedGroups([]);
    setSelectedType("");
    setSelectedField("");
    setTitle("");
    setMessage("");
    setLegal3("");
    setSelectedCategoryId("");
    setSelectedOfferId("");
  };

  const handleEmployeeChange = (event: any, value: any[]) => {
    const selectedEmployeeIds = value.map((group) => group.id);
    setSelectedEmployee(selectedEmployeeIds);
  };

  const handleGroupChange = (event: any, value: any[]) => {
    const selectedGroupIds = value.map((group) => group.groupId);
    setSelectedGroups(selectedGroupIds);
  };

  const handleTypeChange = (event: any) => {
    const { value } = event.target;
    setSelectedType(value);
  };

  const handleFieldChange = (event: any) => {
    const { value } = event.target;
    setSelectedField(value);
  };

  const addNotificationApi = async () => {
    if (!title) {
      showError("Please enter title");
      return;
    }

    if (selectedType === "broadcast" && !message) {
      showError("Please enter Message");
      return;
    }

    if (selectedType === "email" && !legal3) {
      showError("Please enter Message");
      return;
    }

    if (!selectedType) {
      showError("Please select type");
      return;
    }

    if (selectedField === NOTIFICATION_TYPES.OFFER && !selectedCategoryId) {
      showError("Please select category");
      return;
    }

    if (selectedField === NOTIFICATION_TYPES.OFFER && !selectedOfferId) {
      showError("Please select offer");
      return;
    }

    const body = {
      broadCastType: selectedType,
      broadCastTo: selectedField,
      broadCastTitle: title,
      ...(selectedUsers?.length && !selectedUsers?.includes("all")
        ? { broadCastUserId: selectedUsers }
        : {}),
      ...(selectedGroups?.length ? { broadCastGroupIds: selectedGroups } : {}),
      // broadCastCategoryId: "string",
      // broadCastOfferId: "string",
      broadCastMessage: selectedType === "broadcast" ? message : legal3,
      ...(audience ? { broadCastAudience: audience } : {}),
      ...(selectedEmployee?.length
        ? { broadCastUserId: selectedEmployee }
        : {}),
      ...(selectedOfferId ? { broadCastOfferId: selectedOfferId } : {}),
      ...(selectedActiveOffer ? { offerId: selectedActiveOffer } : {}),
    };

    try {
      setLoading(true);

      const response = await addNotification(body).unwrap();

      setLoading(false);

      if (response.status === 200) {
        clear();
        showToast(response?.message || "");
        setOpen(false);
        getNotificationsApi();
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getUsersApi = async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        search: "",
      }).unwrap();

      setLoading(false);

      if (response.status === 200) {
        setUsers(response?.data || []);
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getGroupApi = async () => {
    try {
      const response = await getGroups({
        page: 1,
        search: "",
      }).unwrap();
      if (response.status === 200) {
        setGroupData(response?.data || []);
        // setCount(response?.data?.totalCount);
      } else {
        setGroupData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getCategoryApi = async () => {
    try {
      setLoading(true);
      const response = await getCategory({ search: "" }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setCategoryData(response?.data || []);
      } else {
        setLoading(false);
        setCategoryData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getDeviceSpecificUsers = async () => {
    try {
      const response = await deviceSpecificUsers({
        deviceType:
          selectedField === NOTIFICATION_TYPES.ANDROID_USER
            ? "android"
            : selectedField === NOTIFICATION_TYPES.IOS_USER
              ? "ios"
              : "",
      }).unwrap();
      if (response.status === 200) {
        setDeviceUserData(response?.data || []);

        // setCount(response?.data?.totalCount);
      } else {
        setDeviceUserData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getEmployeesApi = async () => {
    try {
      const response = await employees({}).unwrap();
      if (response.status === 200) {
        setEmployeeData(response?.data || []);
      } else {
        setEmployeeData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getOffersApi = async () => {
    try {
      setLoading(true);
      const response = await getOffers({
        page: 1,
        search: "",
        categoryId: selectedCategoryId,
        status: "active",
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setOfferData(response?.data?.items || []);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getActiveOffersApi = async () => {
    try {
      const response = await getActiveOffers({}).unwrap();
      if (response.status === 200) {
        setActiveOfferData(response?.data || []);
      } else {
        setActiveOfferData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getUsersApi();
    getGroupApi();
    getActiveOffersApi();
  }, []);

  useEffect(() => {
    if (
      selectedField === NOTIFICATION_TYPES.ANDROID_USER ||
      selectedField === NOTIFICATION_TYPES.IOS_USER
    ) {
      getDeviceSpecificUsers();
    }
    if (selectedField === NOTIFICATION_TYPES.EMPLOYEE) {
      getEmployeesApi();
    }
    if (selectedField === NOTIFICATION_TYPES.OFFER) {
      getCategoryApi();
    }
  }, [selectedField]);

  useEffect(() => {
    if (selectedCategoryId) {
      getOffersApi();
    }
  }, [selectedCategoryId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="modal addNotification_modal"
    >
      <div className="modal-dialog" style={{ minWidth: "50%" }}>
        <div className="modal-body">
          <div className="modal_title">
            <h2>Add Notification</h2>
            <div
              role="button"
              tabIndex={0}
              className="btn-close"
              onClick={() => {
                clear();
                setOpen(false);
              }}
            >
              <CloseIcon />
            </div>
          </div>

          <form className="form">
            <div className="gap_p">
              <div className="control_group w_100">
                <TextField
                  variant="outlined"
                  placeholder="Notification Title"
                  fullWidth
                  hiddenLabel
                  value={title}
                  inputProps={{ maxLength: 40 }}
                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setTitle(e.target.value);
                    }
                  }}
                />
              </div>

              <div className="control_group w_50">
                <Select
                  fullWidth
                  name="selectField1"
                  value={selectedField}
                  onChange={handleFieldChange}
                  displayEmpty
                >
                  <MenuItem disabled value="">
                    Select Send To
                  </MenuItem>
                  <MenuItem value="users">User</MenuItem>
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="all">Sub-Admin</MenuItem>
                  <MenuItem value="android">Android User</MenuItem>
                  <MenuItem value="ios">IOS User</MenuItem>
                  <MenuItem value="Groups">Groups</MenuItem>
                  <MenuItem value="offer">Offer</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </div>

              {selectedField === NOTIFICATION_TYPES.USER ? (
                <div className="control_group w_50">
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={[{ userId: "all", name: "All" }, ...users]}
                    value={
                      selectedUsers.includes("all")
                        ? [{ userId: "all", name: "All" }]
                        : users.filter((u) => selectedUsers.includes(u.userId))
                    }
                    onChange={(event, value) => {
                      const hasAll = value?.some((v) => v?.userId === "all");

                      if (hasAll) {
                        if (value.length === 1) {
                          setSelectedUsers(["all"]);
                          setAudience("users");
                        } else {
                          setSelectedUsers(["all"]);
                          setAudience("users");
                        }
                      } else {
                        setSelectedUsers(value?.map((v) => v?.userId));
                        setAudience("");
                      }
                    }}
                    getOptionLabel={(option) => option?.name}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.userId === value?.userId
                    }
                    renderOption={(props, option: any, { selected }) => (
                      <li {...props}>
                        <FormControlLabel
                          control={<Checkbox checked={selected} />}
                          label={option?.name}
                        />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Users" />
                    )}
                  />
                </div>
              ) : selectedField === NOTIFICATION_TYPES.GROUPS ? (
                <div className="control_group w_50">
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={groupData}
                    value={groupData?.filter((option) =>
                      selectedGroups?.includes(option?.groupId)
                    )}
                    onChange={handleGroupChange}
                    getOptionLabel={(option) => option?.groupName}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.groupId === value?.groupId
                    }
                    onOpen={() => setAutoCompleteOpen(true)}
                    onClose={() => setAutoCompleteOpen(false)}
                    renderOption={(props, option: any, { selected }) => {
                      return (
                        <li {...props}>
                          <FormControlLabel
                            control={<Checkbox checked={selected} />}
                            name="option"
                            label={option?.groupName}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Groups" />
                    )}
                  />
                </div>
              ) : selectedField === NOTIFICATION_TYPES.ANDROID_USER ||
                selectedField === NOTIFICATION_TYPES.IOS_USER ? (
                <div className="control_group w_50">
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={[{ id: "all", name: "All" }, ...deviceUserData]}
                    value={
                      selectedUsers?.includes("all")
                        ? [{ id: "all", name: "All" }]
                        : deviceUserData?.filter((u) =>
                            selectedUsers?.includes(u.id)
                          )
                    }
                    onChange={(event, value) => {
                      const ids = value.map((v) => v.id);

                      if (ids?.includes("all")) {
                        // Case: if "all" is already selected, toggle it off
                        if (selectedUsers?.includes("all")) {
                          setSelectedUsers([]);
                          setAudience("");
                        } else {
                          setSelectedUsers(["all"]);
                          setAudience(
                            selectedField === NOTIFICATION_TYPES.ANDROID_USER
                              ? "android"
                              : "ios"
                          );
                        }
                      } else {
                        setSelectedUsers(ids);
                        setAudience("");
                      }
                    }}
                    getOptionLabel={(option) => option?.name}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.id === value?.id
                    }
                    onOpen={() => setAutoCompleteOpen(true)}
                    onClose={() => setAutoCompleteOpen(false)}
                    renderOption={(props, option: any, { selected }) => (
                      <li {...props}>
                        <FormControlLabel
                          control={<Checkbox checked={selected} />}
                          label={option?.name || "-"}
                        />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Users" />
                    )}
                  />
                </div>
              ) : selectedField === NOTIFICATION_TYPES.EMPLOYEE ? (
                <div className="control_group w_50">
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={employeeData}
                    value={employeeData?.filter((option) =>
                      selectedEmployee?.includes(option?.id)
                    )}
                    onChange={handleEmployeeChange}
                    getOptionLabel={(option) => option?.name}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.id === value?.id
                    }
                    onOpen={() => setAutoCompleteOpen(true)}
                    onClose={() => setAutoCompleteOpen(false)}
                    renderOption={(props, option: any, { selected }) => {
                      return (
                        <li {...props}>
                          <FormControlLabel
                            control={<Checkbox checked={selected} />}
                            name="option"
                            label={option?.name || "-"}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Users" />
                    )}
                  />
                </div>
              ) : selectedField === NOTIFICATION_TYPES.OFFER ? (
                <>
                  <div className="control_group w_50">
                    <Select
                      fullWidth
                      value={selectedCategoryId}
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem disabled value="">
                        Select Category
                      </MenuItem>
                      {categoryData?.map((cat) => (
                        <MenuItem key={cat?.id} value={cat?.id}>
                          {cat?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className="control_group w_50">
                    <Select
                      fullWidth
                      value={selectedOfferId}
                      onChange={(e) => setSelectedOfferId(e.target.value)}
                      displayEmpty
                      disabled={!selectedCategoryId}
                    >
                      <MenuItem disabled value="">
                        Select Offer
                      </MenuItem>
                      {offerData?.map((item: any) => (
                        <MenuItem key={item?.id} value={item?.id}>
                          {item?.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </>
              ) : null}

              {/* Conditional Multi-Select */}
              {/* {formvalue.selectField1 !== "" && (
                <div className="control_group w_50">
                  <Select
                    fullWidth
                    multiple
                    displayEmpty
                    name="selectedRoles"
                    value={formvalue.selectedRoles}
                    onChange={handleMultiSelectChange}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <span>{getRolePlaceholder()}</span>;
                      }
                      return currentOptions
                        .filter((r) => selected.includes(r.value))
                        .map((r) => r.label)
                        .join(", ");
                    }}
                  >
                    <MenuItem disabled value="">
                      <span>{getRolePlaceholder()}</span>
                    </MenuItem>

                    <MenuItem value="all" className="multi_check_select">
                      <Checkbox
                        checked={
                          formvalue.selectedRoles.length ===
                          currentOptions.length
                        }
                        indeterminate={
                          formvalue.selectedRoles.length > 0 &&
                          formvalue.selectedRoles.length < currentOptions.length
                        }
                      />
                      Select All
                    </MenuItem>

                    {currentOptions.map((role) => (
                      <MenuItem
                        key={role.value}
                        value={role.value}
                        className="multi_check_select"
                      >
                        <Checkbox
                          checked={formvalue.selectedRoles.includes(role.value)}
                        />
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )} */}
              {/* {formvalue.selectField1 === "10" && (
                <>
                  <div className="control_group w_50">
                    <Select
                      fullWidth
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem disabled value="">
                        Select Category
                      </MenuItem>
                      {categoryOptions.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className="control_group w_50">
                    <Autocomplete
                      disablePortal
                      options={offerOptions}
                      sx={{ width: 300 }}
                      renderInput={(params) => (
                        <TextField
                          placeholder="Select Offer Name"
                          hiddenLabel
                          {...params}
                        />
                      )}
                    />
                  </div>
                </>
              )} */}

              {/* Type Selector */}
              <div className="control_group w_50">
                <Select
                  fullWidth
                  name="type"
                  value={selectedType}
                  onChange={handleTypeChange}
                  displayEmpty
                >
                  <MenuItem disabled value="">
                    Select Type
                  </MenuItem>
                  <MenuItem value="broadcast">Broadcast</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                </Select>
              </div>

              {(selectedField === NOTIFICATION_TYPES.USER ||
                selectedField === NOTIFICATION_TYPES.ANDROID_USER ||
                selectedField === NOTIFICATION_TYPES.IOS_USER) &&
              selectedType === "broadcast" ? (
                <div className="control_group w_50">
                  <Select
                    fullWidth
                    name="type"
                    value={selectedActiveOffer}
                    onChange={(e) => setSelectedActiveOffer(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem disabled value="">
                      Offer
                    </MenuItem>
                    {activeOfferData?.map((offer: any) => (
                      <MenuItem key={offer?.id} value={offer?.id}>
                        {offer?.title}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              ) : null}

              {selectedType === "broadcast" && (
                <div className="control_group">
                  <TextField
                    placeholder="Enter Note"
                    fullWidth
                    hiddenLabel
                    multiline
                    minRows={3}
                    value={message}
                    inputProps={{ maxLength: 40 }}
                    onChange={(e) => {
                      if (e.target.value === " " || e.target.value === ".") {
                      } else {
                        setMessage(e.target.value);
                      }
                    }}
                  />
                </div>
              )}

              {selectedType === "email" && (
                <div className="control_group w_100">
                  <EditText content={legal3} setContent={setLegal3} />
                </div>
              )}
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => {
                  clear();
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  addNotificationApi();
                  setError(true);
                }}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddNotification;
